import React, { useRef, useMemo } from 'react';

import { Box, Flex, Text, Center } from '@chakra-ui/react';
import { DonutChart } from '@mesh.ai/rabbit';
import {
  ChartErrorScreenWithoutPaperShadow,
  ChartLoadingWithoutPaperShadow,
} from 'Deer/intent/DisciplinaryActions/components/GraphSection/GraphLoadingSections';
import { useGetDisciplinaryActions } from 'Deer/pages/MyData/DisciplinaryActions/dataHooks';
import { DISCIPLINARY_ACTIONS_STATUS, MY_COMPANY } from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';

import { Download } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { ExpandableContainerProvider } from 'uiToolkitV1/ExpandableContainerProvider';
import { useSelectIsExpanded } from 'uiToolkitV1/ExpandableContainerProvider/ExpandedContainerSlice';
import { TextHeader3 } from 'uiToolkitV1/Typography';

// Define the interface for the chart ref
interface DownloadRef {
  download: (filename?: string) => void;
}

interface DonutChartDataType {
  label: string;
  value: number;
  color: string;
  labelColor: string;
  count: number;
}

interface ChartContentProps {
  isFetching: boolean;
  isError: boolean;
  chartData: DonutChartDataType[];
  isExpanded: boolean;
  chartRef: React.RefObject<DownloadRef>;
}

const ChartContent: React.FC<ChartContentProps> = ({ isFetching, isError, chartData, isExpanded, chartRef }) => {
  if (isFetching) {
    return <ChartLoadingWithoutPaperShadow width="100%" />;
  }

  if (isError) {
    return <ChartErrorScreenWithoutPaperShadow width="100%" />;
  }

  if (!chartData.length) {
    return (
      <Center height="100%">
        <Text color="text.g2">No violation data available for the past 6 months</Text>
      </Center>
    );
  }

  return (
    <Box
      id="chart-container"
      className="rabbit-ds"
      style={{
        height: isExpanded ? 'calc(100% - 100px)' : '250px',
        width: '100%',
      }}
      sx={
        isExpanded
          ? {
              '.text-body-secondary': {
                fontSize: '20px!important',
              },
              '.rounded-radius-round': {
                width: '20px !important',
                height: '20px !important',
              },
            }
          : {}
      }
    >
      <DonutChart tooltipTitle="Disciplinary Actions" legendTitle="Distribution" data={chartData} ref={chartRef} />
    </Box>
  );
};

const filterDataFromPast6Months = (rows = []) =>
  rows.filter((row) => {
    if (!row?.REPORTED_TIME) return false;

    const reportedTime = new Date(row.REPORTED_TIME);
    if (Number.isNaN(reportedTime.getTime())) {
      // Skip records with invalid dates
      return false;
    }

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return reportedTime >= sixMonthsAgo;
  });

// Convert our data format to the format expected by DonutChart
const convertToDonutChartData = (data) => {
  if (!data?.length) {
    return [];
  }

  const pendingViolations = data.filter((row) => row?.STATUS === DISCIPLINARY_ACTIONS_STATUS.CREATED).length;
  const approvedViolations = data.filter(
    (row) =>
      row?.STATUS === DISCIPLINARY_ACTIONS_STATUS.APPROVED ||
      row?.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE ||
      row?.STATUS === DISCIPLINARY_ACTIONS_STATUS.ACKNOWLEDGED
  ).length;
  const deniedViolations = data.filter((row) => row?.STATUS === DISCIPLINARY_ACTIONS_STATUS.DENIED).length;
  const totalViolations = pendingViolations + approvedViolations + deniedViolations;

  // Prevent division by zero
  if (totalViolations === 0) {
    return [];
  }

  // convert the percentage to 2 decimal places
  const pendingViolationsPercentage = (pendingViolations / totalViolations) * 100;
  const approvedViolationsPercentage = (approvedViolations / totalViolations) * 100;
  const deniedViolationsPercentage = (deniedViolations / totalViolations) * 100;

  return [
    {
      label: 'No. of violations pending',
      value: Number(pendingViolationsPercentage.toFixed(2)),
      color: '#FFDE88',
      labelColor: '#FFDE88',
      count: pendingViolations,
    },
    {
      label: 'No. of violations approved',
      value: Number(approvedViolationsPercentage.toFixed(2)),
      color: '#91CA7D',
      labelColor: '#91CA7D',
      count: approvedViolations,
    },
    {
      label: 'No. of violations denied',
      value: Number(deniedViolationsPercentage.toFixed(2)),
      color: '#FD9B9B',
      labelColor: '#FD9B9B',
      count: deniedViolations,
    },
  ];
};

const CHART_TITLE = 'Employees with violations (Past 6 months)';

const AdminDisciplinaryActionsPieChart = () => {
  const chartRef = useRef<DownloadRef>(null);
  const isExpanded = useSelectIsExpanded();

  const {
    data: disciplinaryActionsData,
    isError,
    isFetching,
  } = useGetDisciplinaryActions({
    cohortId: MY_COMPANY.id,
    status: undefined,
  });

  const { rows = [] } = disciplinaryActionsData ?? {};

  const disciplinaryActionsDataPast6Months = useMemo(() => filterDataFromPast6Months(rows), [rows]);

  const chartData = useMemo(
    () => convertToDonutChartData(disciplinaryActionsDataPast6Months),
    [disciplinaryActionsDataPast6Months]
  );

  const handleDownload = () => {
    if (chartRef?.current) {
      chartRef.current.download(`${CHART_TITLE}_${new Date().toISOString().split('T')[0]}`);
    }
  };

  return (
    <Box>
      <ExpandableContainerProvider id={CHART_TITLE} toggleBtnBoxProps={{ top: '17px !important', zIndex: 1 }}>
        <Flex
          id="myChart"
          width="100%"
          height="364px"
          justify="center"
          align="center"
          borderRadius="8px"
          padding="0px 12px 0px 12px"
          backgroundColor="white"
          position="relative"
          boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
        >
          <Flex h="100%" w="100%" flexDirection="column" m={isExpanded ? '0px 12px' : '0px'}>
            <Flex m="14px 0px">
              <TextHeader3 bold color="text.g1" ml="6px">
                {CHART_TITLE}
              </TextHeader3>
              <Flex m="2px 44px 0 auto">
                <Button variant="tertiary" EndIcon={Download} onClick={handleDownload} isDisabled={!chartData.length} />
              </Flex>
            </Flex>
            <ChartContent
              isFetching={isFetching}
              isError={isError}
              chartData={chartData}
              isExpanded={isExpanded}
              chartRef={chartRef}
            />
          </Flex>
        </Flex>
      </ExpandableContainerProvider>
    </Box>
  );
};

export { AdminDisciplinaryActionsPieChart };
